/**
 * @file index.jsx
 * @author Rishikesh
 * @date 2024-09-09
 * @description Login page of this app.
 */
import { Box, Typography, colors } from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { signIn } from "../../../store/features/authSlice";
import { LoginForm } from "./LoginForm";
import { useFetchData } from "../../../components/hooks/useFetchData";
import { CrossroadsLogo } from "../../../assets/images/CrossroadsLogo";
import { AuthBackground } from "../../../components/common/AuthBackground";
import { LoadingOverlay } from "../../../components/common/LoadingOverlay";

const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [onRequest, setOnRequest] = useState(false);
  const [loginProgress, setLoginProgress] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const { fetchData } = useFetchData();

  const onSignIn = async (data, setError) => {
    const { email, password } = data;
    const body = {
      email,
      password,
    };
    const result = await fetchData("login", { method: "post", body: body });
    if (result?.code === 200) {
      // Simulate login progress
      setOnRequest(true);
      const interval = setInterval(() => {
        setLoginProgress((prev) => prev + 100 / 40);
      }, 50);
      setTimeout(() => {
        clearInterval(interval);
      }, 2000);
      setTimeout(() => {
        setIsLoggedIn(true);
      }, 2100);
      setTimeout(() => {
        dispatch(
          signIn({
            user: { email, role: result.data.role, name: result.data.name },
            ssid: result.data.ssid,
          })
        );
        navigate("/dashboard");
      }, 3300);
    } else {
      setError("root", {
        type: "manual",
        message: result?.message || "Invalid credentials",
      });
      return;
    }
  };

  return (
    <Box
      position="relative"
      width="100%"
      height="100vh"
      sx={{ "::-webkit-scrollbar": { display: "none" } }}
    >
      <AuthBackground />
      <Box
        sx={{
          position: "relative",
          left: 0,
          height: "100%",
          width: isLoggedIn
            ? "100%"
            : { xl: "30%", lg: "40%", md: "50%", xs: "100%" },
          transition: "all 1s ease-in-out",
          bgcolor: colors.common.white,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            opacity: isLoggedIn ? 0 : 1,
            transition: "all 0.3s ease-in-out",
            height: "100%",
            "::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Box
            sx={{
              textAlign: "center",
              p: 5,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CrossroadsLogo width="100%" height={100} />
          </Box>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              "::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Box
              sx={{
                textAlign: "center",
                p: 5,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography component="h1" variant="h5">
                Sign in
              </Typography>
            </Box>
            <LoginForm onSubmit={onSignIn} />
          </Box>
          {onRequest && <LoadingOverlay loginProgress={loginProgress} />}
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
